import { getPersistentDPID } from './uuid';

export type SamsungProductInfo = {
  model?: string | null;
  modelCode?: string | null;
  realModel?: string | null;
  localSet?: string | null;
  firmware?: string | null;
  version?: string | null;
  smartTVServerType?: string | null;
  duid?: string | null;
};

export type SamsungAdsInfo = {
  tifa?: string | null;
  isLatEnabled?: boolean | null;
};

/**
 * Resolves the identifier to use for Samsung TVs.
 *
 * ✔ If `tifa` is available → use it
 * ✔ If missing → fallback to persistent DPID (stored in localStorage)
 *
 * @function resolveSamsungId
 * @param {string | null} tifa - The TIFA identifier retrieved from Samsung Ads API.
 * @returns {{ tvIdType: 'tifa' | 'dpid'; tvId: string }}
 *   - `tvIdType`: chosen identifier type
 *   - `tvId`: actual value to use
 */
export const resolveSamsungId = (tifa: string | null) => {
  if (tifa) {
    return {
      tvIdType: 'tifa' as const,
      tvId: tifa,
    };
  }

  return {
    tvIdType: 'dpid' as const,
    tvId: getPersistentDPID(),
  };
};

/**
 * Retrieves Samsung product info using the `webapis.productinfo` API.
 *
 * ✔ Returns `null` if API is unavailable
 * ✔ Returns object with available product info
 *
 * @function getSamsungInfo
 * @returns {Partial<SamsungProductInfo> | null}
 */
export const getSamsungInfo = (): Partial<SamsungProductInfo> | null => {
  try {
    const tvSamsungInfo = window.webapis?.productinfo;
    if (!tvSamsungInfo) return null;

    return {
      model: tvSamsungInfo.getModel?.() ?? null,
      modelCode: tvSamsungInfo.getModelCode?.() ?? null,
      realModel: tvSamsungInfo.getRealModel?.() ?? null,
      localSet: tvSamsungInfo.getLocalSet?.() ?? null,
      firmware: tvSamsungInfo.getFirmware?.() ?? null,
      version: tvSamsungInfo.getVersion?.() ?? null,
      duid: tvSamsungInfo.getDuid?.() ?? null,
    };
  } catch {
    return null;
  }
};

/**
 * Retrieves Samsung Ads info using the `webapis.adinfo` API.
 *
 * ✔ Returns `null` values if API is unavailable or fails
 * ✔ Returns `tifa` and `isLatEnabled` flags when available
 *
 * @function getSamsungAdsInfo
 * @returns {Partial<SamsungAdsInfo>}
 */
export const getSamsungAdsInfo = (): Partial<SamsungAdsInfo> => {
  const adInfo = window.webapis?.adinfo;
  if (!adInfo) {
    return { tifa: null, isLatEnabled: null };
  }

  let tifa: string | null = null;
  let isLatEnabled: boolean | null = null;

  try {
    const value = adInfo.getTIFA?.();
    tifa = typeof value === 'string' ? value : null;
  } catch (err) {
    tifa = null;
    console.error('[ERROR] TV Samsung at getting tifa: ', err);
  }

  try {
    const value = adInfo.isLATEnabled?.();
    isLatEnabled = typeof value === 'boolean' ? value : null;
  } catch (err) {
    isLatEnabled = null;
    console.error('[ERROR] TV Samsung at getting is lat enabled: ', err);
  }

  return {
    tifa,
    isLatEnabled,
  };
};
